/* Premier Courses — course enroll rail.
   Wires the tier radios to:
     • every enrol CTA's href — a cart permalink /cart/<variant>:1, the same
       mechanism the pricing cards use. pc-cart.js intercepts the click and
       routes it to the cart review step; the raw permalink (which replaces the
       cart and goes straight to checkout) remains the no-JS fallback
     • the displayed "Enroll — $price" label and the mobile sticky-bar price
     • the highlighted column in the course-matrix comparison grid, which is a
       SEPARATE section, hence the document-level lookup rather than a scoped
       one
     • the visible panel in the course-curriculum list, when a course's
       packages include different things — also its own section, same reason
     • the highlighted column in the price-comparison grid — same reason again
     • the ?variant= parameter in the address bar, so a chosen tier survives a
       refresh and can be shared — the page already READS that parameter
       server-side, this is the write half
   and makes both grids' package bars selectable: they are the part of each
   grid that stays on screen, so clicking a package name there checks the
   matching radio and everything above re-syncs from it
   and repoints "Compare all packages" at the comparison grid when the page
   has one, so it scrolls rather than navigating away.
   and toggles a sticky bottom bar on mobile once the main rail CTA scrolls
   out of view. Pure vanilla JS, idempotent, theme-editor aware. */
(function () {
  'use strict';

  /* Record the chosen tier in the address bar.

     course-enroll.liquid picks the checked radio from
     product.selected_or_first_available_variant, which Shopify fills from
     ?variant=. So the page already understands that parameter on the way in —
     the pricing cards deep-link with it. Writing it back closes the loop: a
     refresh keeps the tier, and a copied URL carries the tier the sender was
     actually looking at.

     replaceState, not pushState: the tier bar is one control, not a sequence
     of pages. Pushing would make Back step through every tier the buyer tried
     before it left the product, which is worse than not touching history.

     Called only from a real change event — syncTier also runs at init, and
     stamping ?variant= onto every clean page load would rewrite the canonical
     URL for everyone who never touched the control. */
  function recordTierInUrl(value) {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', value);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) { /* no URL constructor — selection still works, just not shareable */ }
  }

  function initEnroll(root) {
    if (!root || root.dataset.pcEnrollInit === '1') return;
    root.dataset.pcEnrollInit = '1';

    var form = root.querySelector('[data-pc-enroll-form]');
    if (!form) return;

    var radios = Array.prototype.slice.call(form.querySelectorAll('[data-pc-tier-radio]'));
    var tiers = Array.prototype.slice.call(form.querySelectorAll('[data-pc-tier]'));
    var priceEl = root.querySelector('[data-pc-enroll-price]');
    var cta = root.querySelector('[data-pc-enroll-cta]');

    // Mobile sticky bar lives outside the rail root (sibling). Find it by section.
    var sectionRoot = root.closest('.shopify-section') || document;
    var sticky = sectionRoot.querySelector('[data-pc-enroll-sticky]');
    var stickyPrice = sticky ? sticky.querySelector('[data-pc-sticky-price]') : null;

    // Both the rail CTA and the mobile bar CTA carry the permalink.
    var links = Array.prototype.slice.call(
      sectionRoot.querySelectorAll('[data-pc-enroll-link]'));

    // The matrix lives in its own section, so it is not under sectionRoot.
    var matrixCells = Array.prototype.slice.call(
      document.querySelectorAll('[data-pc-matrix-col]'));

    // Ditto the curriculum panels. Absent on single-tier courses, and on any
    // course whose packages all include the same thing — both render one flat
    // list with no panels, so this is simply empty.
    var curriculumPanels = Array.prototype.slice.call(
      document.querySelectorAll('[data-pc-curriculum-panel]'));

    // Ditto the price-comparison grid, whose columns highlight like the
    // matrix's — its package bar and every price and feature badge all carry
    // the hook, so one query covers the lot.
    var vsCells = Array.prototype.slice.call(
      document.querySelectorAll('[data-pc-vs-col]'));

    // Package-bar buttons in BOTH grids. They are a second way to reach the
    // radios rather than a second control, so they carry aria-pressed and the
    // radios stay the source of truth.
    var pkgButtons = Array.prototype.slice.call(
      document.querySelectorAll('[data-pc-select-variant]'));

    // "Compare all packages" defaults to the course's marketing page. When the
    // comparison grid is on THIS page, scrolling to it beats leaving for a page
    // that shows the same table. Anchor navigation handles the rest: base.css
    // already sets scroll-behavior: smooth (with a reduced-motion guard) and
    // #pc-compare carries scroll-margin-top for the sticky header.
    // Untouched when the grid is absent — CE and reactivation have no grid, so
    // they keep the marketing-page link, as does anyone without JS.
    var compare = sectionRoot.querySelector('.pc-course-enroll__compare');
    if (compare && document.getElementById('pc-compare')) {
      compare.setAttribute('href', '#pc-compare');
    }

    function syncTier(radio) {
      if (!radio) return;
      var price = radio.getAttribute('data-price') || '';

      links.forEach(function (a) {
        a.setAttribute('href', '/cart/' + radio.value + ':1');
      });

      // Shift the comparison grid's highlight onto the selected tier.
      matrixCells.forEach(function (cell) {
        var match = cell.getAttribute('data-pc-matrix-col') === String(radio.value);
        cell.classList.toggle('is-active', match);
      });
      // Swap "What you'll study" to the selected package's contents.
      curriculumPanels.forEach(function (panel) {
        var match = panel.getAttribute('data-pc-curriculum-panel') === String(radio.value);
        panel.hidden = !match;
      });
      // Shift the competitor grid's highlight onto the selected package.
      vsCells.forEach(function (cell) {
        var match = cell.getAttribute('data-pc-vs-col') === String(radio.value);
        cell.classList.toggle('is-active', match);
      });
      // Both bars' buttons announce which package is chosen. The tint they
      // already get rides on is-active above; this is the part a screen reader
      // hears.
      pkgButtons.forEach(function (btn) {
        var match = btn.getAttribute('data-pc-select-variant') === String(radio.value);
        btn.setAttribute('aria-pressed', String(match));
      });

      if (priceEl && price) priceEl.textContent = price;
      if (stickyPrice && price) stickyPrice.textContent = price;

      // visual selected state on the label wrappers
      tiers.forEach(function (t) {
        var input = t.querySelector('[data-pc-tier-radio]');
        if (input === radio) { t.classList.add('is-on'); }
        else { t.classList.remove('is-on'); }
      });

      // An available selection means the CTA is live. The CTA is an anchor
      // now, and `disabled` is meaningless on one — a sold-out tier has to
      // lose its href and be flagged to assistive tech instead.
      if (cta) {
        if (radio.disabled) {
          cta.setAttribute('aria-disabled', 'true');
          links.forEach(function (a) { a.removeAttribute('href'); });
        } else {
          cta.removeAttribute('aria-disabled');
        }
      }
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        syncTier(radio);
        recordTierInUrl(radio.value);
      });
    });

    // Clicking a package in either bar selects it. Dispatching `change` rather
    // than calling syncTier directly keeps the radio the single entry point —
    // anything else listening to the group hears it too. A sold-out tier is
    // left alone, same as the CTA is.
    pkgButtons.forEach(function (btn) {
      if (btn.dataset.pcSelectBound === '1') return;
      btn.dataset.pcSelectBound = '1';
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-pc-select-variant');
        var target = null;
        radios.forEach(function (r) { if (String(r.value) === id) target = r; });
        if (!target || target.disabled || target.checked) return;
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    // Establish initial state from whichever radio is checked.
    var initial = radios.filter(function (r) { return r.checked; })[0] || radios[0];
    syncTier(initial);

    // ── Mobile sticky bottom bar: show once the inline CTA scrolls away. ──
    if (sticky && cta && 'IntersectionObserver' in window) {
      var isMobile = function () { return window.matchMedia('(max-width: 1040px)').matches; };

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!isMobile()) { sticky.hidden = true; return; }
          // Hide the bar while the real CTA is on screen; show it otherwise.
          sticky.hidden = entry.isIntersecting;
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });

      io.observe(cta);

      // Re-evaluate on resize: never show the bar on desktop widths.
      window.addEventListener('resize', function () {
        if (!isMobile()) sticky.hidden = true;
      }, { passive: true });
    }
  }

  function initAll(scope) {
    var ctx = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.slice
      .call(ctx.querySelectorAll('[data-pc-enroll]'))
      .forEach(initEnroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    // The enroll rail may load as its own section or nested in the hero.
    initAll(e.target);
  });
})();
